import React, { useEffect, useState } from "react";
import { reApi, errMsg } from "@/lib/residentApi";
import { EditToggle } from "./ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Row } from "./ProfileTab";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const words = (s) => (s ? String(s).replace(/_/g, " ") : "—");
const labelCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted mb-2";
const PHASE = { phase_1: "Phase 1", phase_2: "Phase 2", phase_3: "Phase 3" };

const Section = ({ title, testId, children }) => (
  <div data-testid={testId} className="space-y-2">
    <div className={labelCls}>{title}</div>
    {children}
  </div>
);

const ansText = (a) => (a == null ? "" : typeof a === "object" ? String(a.value ?? a.text ?? a.answer ?? "") : String(a));

function ResendRoadmap({ rid, stage, onDone }) {
  const [busy, setBusy] = useState(false);
  if (["complete", "completed"].includes(stage.status)) return null;
  const go = async () => {
    setBusy(true);
    try { await reApi.post(`/residents/${rid}/roadmap/${stage.stage}/resend`); toast.success("Roadmap resent · it's on their PACT Life Pass Homework now"); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return <button type="button" onClick={go} disabled={busy} data-testid={`roadmap-resend-${stage.stage}`} className="ml-auto text-[11px] border border-nex-gold/50 text-nex-gold px-2 py-0.5 hover:bg-nex-gold/10 disabled:opacity-50">{busy ? "Resending…" : "Resend roadmap"}</button>;
}

const Stabilization = ({ stage, rid, onChanged }) => (
  <div data-testid={`roadmap-stage-${stage.stage}`} className="border border-white/10 p-3 space-y-2">
    <div className="flex items-center gap-2 text-sm text-nex-silver"><span>{stage.title || words(stage.stage)} · <span className="text-nex-gold">{words(stage.status)}</span></span><ResendRoadmap rid={rid} stage={stage} onDone={onChanged} /></div>
    <div className="text-xs text-nex-muted">
      Sent {fmt(stage.sent_at)} by {stage.sent_by || "—"} · {stage.reminder_count} reminders (last {fmt(stage.reminder_last_at)}) · completed {fmt(stage.completed_at)}
    </div>
    {stage.questions.map((x, i) => (
      <div key={i} data-testid={`roadmap-stage-q-${i}`} className="text-xs py-1 border-b border-white/5">
        <span className="text-nex-silver">{x.label}:</span> <span className="text-nex-muted">{x.prompt}</span>
        <div data-testid={`roadmap-stage-a-${stage.stage}-${i}`} className={ansText(x.answer) ? "text-nex-silver mt-0.5" : "text-nex-muted italic mt-0.5"}>{ansText(x.answer) || "Not answered"}{x.answer?.accepted === false && <span className="text-amber-300 not-italic"> · needs more</span>}</div>
      </div>
    ))}
  </div>
);

const Milestone = ({ m, i }) => {
  const [open, setOpen] = useState(false);
  const done = (m.action_steps || []).filter((a) => a.done).length;
  return (
    <div data-testid={`roadmap-milestone-${i}`} className="border border-white/10">
      <button data-testid={`roadmap-milestone-toggle-${i}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 p-2 text-left text-sm text-nex-silver hover:bg-white/[0.03] transition-colors">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="flex-1">{m.milestone_title}</span>
        <span className="text-xs text-nex-muted">{m.current_score}→{m.target_score} · {done}/{(m.action_steps || []).length} steps · {m.status}</span>
      </button>
      {open && (
        <div data-testid={`roadmap-milestone-detail-${i}`} className="px-3 pb-3 space-y-2 text-xs text-nex-muted">
          <p>{m.narrative}</p>
          <p><span className="text-nex-silver">Success criteria:</span> {m.success_criteria}</p>
          <ul className="space-y-1">
            {(m.action_steps || []).map((a, j) => <li key={j}>{a.done ? "☑" : "☐"} {a.text}</li>)}
          </ul>
          <p>Created {fmt(m.created_at)}{m.completed_at ? ` · completed ${fmt(m.completed_at)} by ${m.completed_by || "—"}` : ""}</p>
        </div>
      )}
    </div>
  );
};

function EditScores({ rid, answers, onDone, onCancel }) {
  const [rows, setRows] = useState(answers.map((a) => ({ ...a })));
  const [err, setErr] = useState(null);
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: Number(v) } : r)));
  const save = async () => {
    setErr(null);
    try { await reApi.patch(`/residents/${rid}/baseline/answers`, { answers: rows.map(({ question_no, current, target }) => ({ question_no, current, target })) }); onDone(); }
    catch (e) { setErr(errMsg(e)); }
  };
  const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <div data-testid="roadmap-edit-scores" className="space-y-2">
      <p className="text-[11px] text-nex-muted">Correct any score (1–10). Each change is kept in the baseline's edit history.</p>
      <div className="grid sm:grid-cols-2 gap-x-4">
        {rows.map((a, i) => (
          <div key={a.question_no} className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto_auto] items-center gap-2 text-xs text-nex-silver py-1 border-b border-white/5">
            <span className="text-nex-muted">{a.question_no}</span><span className="min-w-0 break-words">{a.title}</span>
            {["current", "target"].map((k) => (
              <select key={k} aria-label={`${a.title} ${k}`} data-testid={`roadmap-edit-${k}-${a.question_no}`} value={a[k] || ""} onChange={(e) => set(i, k, e.target.value)} className="bg-black/40 border border-white/10 rounded-sm px-1 py-0.5 text-nex-silver">
                {nums.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            ))}
          </div>
        ))}
      </div>
      {err && <p data-testid="roadmap-edit-error" className="text-xs text-red-300">{err}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="roadmap-edit-cancel" className="px-3 py-1.5 border border-white/10 text-nex-muted font-mono text-[10px] uppercase tracking-[0.18em] rounded-sm">Cancel</button>
        <button type="button" onClick={save} data-testid="roadmap-edit-save" className="px-3 py-1.5 bg-nex-gold text-black font-mono text-[10px] uppercase tracking-[0.18em] rounded-sm">Save scores</button>
      </div>
    </div>
  );
}

const Baseline = ({ b }) => (
  <>
    <div className="text-xs text-nex-muted">Completed {fmt(b.completed_at)} by {b.completed_by || "—"} · {b.version}</div>
    <div className="grid sm:grid-cols-3 gap-3">
      {Object.entries(b.pillar_summary || {}).map(([k, s]) => (
        <Row key={k} label={s.label} testId={`roadmap-baseline-summary-${k}`}>{`Avg ${s.avg_current} now → ${s.avg_target} target · gap ${s.avg_gap}`}</Row>
      ))}
    </div>
    <div data-testid="roadmap-baseline-scores" className="grid sm:grid-cols-2 gap-x-4">
      {(b.answers || []).map((a) => (
        <div key={a.question_no} className="flex flex-wrap text-xs text-nex-silver py-0.5 border-b border-white/5">
          <span className="text-nex-muted w-6">{a.question_no}</span><span className="flex-1 min-w-0 pr-2">{a.title}</span>
          <span className="text-nex-muted">{a.current} → {a.target}{a.gap ? ` (gap ${a.gap})` : ""}</span>
          {a.narrative && <p className="basis-full text-[11px] text-nex-muted italic pl-6 pb-1">"{a.narrative}"</p>}
        </div>
      ))}
    </div>
  </>
);

function BaselineProgress({ rid }) {
  const [p, setP] = useState(undefined);
  useEffect(() => { reApi.get(`/residents/${rid}/baseline`).then((d) => setP(d.current ? null : d.in_progress)).catch(() => setP(null)); }, [rid]);
  if (!p) return null;
  return <p data-testid="roadmap-baseline-progress" className="text-sm text-amber-200">Baseline in progress: {p.answered} of {p.of} answered{p.updated_at ? ` · last saved ${fmt(p.updated_at)}` : ""}. Their portal stays locked until it's done.</p>;
}

export default function RoadmapTab({ resident, onChanged }) {
  const r = resident.roadmap;
  const [editing, setEditing] = useState(false);
  const [c2c, setC2c] = useState(undefined);
  useEffect(() => { reApi.get(`/residents/${resident.id}/baseline`).then((d) => setC2c(d.current)).catch(() => setC2c(null)); }, [resident.id, editing]);
  const answers = c2c?.answers || r?.baseline?.answers || [];
  const editor = editing && <EditScores rid={resident.id} answers={answers} onCancel={() => setEditing(false)} onDone={() => { setEditing(false); onChanged?.(); }} />;
  const toggle = answers.length > 0 && <EditToggle editing={editing} setEditing={setEditing} label="Edit roadmap answers" testId="roadmap" />;
  if (!r || !r.baseline && !(r.milestones || []).length) return (
    <div className="space-y-2">{toggle}{editor}<BaselineProgress rid={resident.id} />
      <p data-testid="roadmap-empty" className="text-sm text-nex-muted">No baseline yet. The resident completes their 30-question baseline the first time they open their portal.</p></div>);
  const phases = [...new Set((r.milestones || []).map((m) => m.phase_id))];
  return (
    <div data-testid="roadmap-tab" className="space-y-6">
      {toggle}
      {editor}
      {r.imported_at && <p className="text-[11px] text-nex-muted">Milestones & stabilization imported from the old platform {fmt(r.imported_at)}</p>}
      <Section title="Milestones" testId="roadmap-milestones">
        {phases.length ? phases.map((p) => (
          <div key={p} data-testid={`roadmap-phase-${p}`} className="space-y-1.5">
            <div className="text-xs text-nex-gold">{PHASE[p] || words(p)} · {r.milestones.find((m) => m.phase_id === p).category_label}</div>
            {r.milestones.map((m, i) => m.phase_id === p && <Milestone key={i} m={m} i={i} />)}
          </div>
        )) : <div className="text-sm text-nex-muted">None</div>}
      </Section>
      <Section title="Starting self-assessment" testId="roadmap-baseline">
        {r.baseline ? <Baseline b={r.baseline} /> : <><BaselineProgress rid={resident.id} /><div className="text-sm text-nex-muted">Not done yet</div></>}
      </Section>
      <Section title="Stabilization roadmap" testId="roadmap-stabilization">
        {(r.stabilization || []).length ? r.stabilization.map((s) => <Stabilization key={s.stage} stage={s} rid={resident.id} onChanged={onChanged} />)
          : <div className="text-sm text-nex-muted">None</div>}
      </Section>
    </div>
  );
}
